import React, { useState, useEffect } from 'react';
import {
    X,
    User,
    BookOpen,
    Award,
    Globe,
    School,
    FileText,
    Save,
    Plus,
    Trash2,
    Calendar,
    Key,
    Download,
    Upload
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import LeadDetailModal from './LeadDetailModal';
import { useAuth } from '../../contexts/AuthContext';

// Interfaces based on the image structure
interface ExamResult {
    id: string;
    exam_date: string;
    exam_name: string;
    score: string;
    note?: string;
}

interface LanguageDoc {
    id: string;
    name: string;
    level: string;
    note?: string;
}

interface SchoolApplication {
    id: string;
    school_name: string;
    department: string;
    username?: string;
    password?: string;
    status: 'pending' | 'completed' | 'rejected' | 'accepted' | 'application_made' | 'enrolled';
    payment_status: string; // e.g. "15 EURO ÖDENDİ"
    result_status: string; // e.g. "KABUL/RED"
    university_id?: string; // Optional ID if matched from DB
}



interface UniversitalyInfo {
    id: string;
    username?: string;
    password?: string;
    school?: string;
    department?: string;
    status: string; // Changed from 'pending' | 'completed' to allow new statuses
}

// Personality Test Types
interface PersonalityTest {
    id: string;
    title: string;
    description: string | null;
    test_type: string;
    scoring_formula: string;
}

interface PTUserAccount {
    id: string;
    email: string;
    full_name: string;
}

interface AssignedTest {
    id: string;
    test_id: string;
    test_title?: string;
    is_completed: boolean;
    assigned_at: string;
    completed_at: string | null;
}

interface TestResultData {
    student_test_id: string;
    result_data: any;
    created_at: string;
}

// Document Categories
const DOCUMENT_CATEGORIES = [
    { key: 'cv', label: 'CV', icon: '📄' },
    { key: 'diploma', label: 'DİPLOMA', icon: '🎓' },
    { key: 'language_cert', label: 'DİL BELGESİ', icon: '🌍' },
    { key: 'transcript', label: 'TRANSKRİPT', icon: '📋' },
    { key: 'yks_result', label: 'YKS SONUÇ', icon: '📊' },
    { key: 'photo', label: 'PHOTO', icon: '📸' },
    { key: 'residence', label: 'İKAMETGAH', icon: '🏠' },
    { key: 'id_card', label: 'ID', icon: '🪪' },
    { key: 'passport', label: 'PASAPORT', icon: '🛂' }
];

interface StudentDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    studentId: string;
    onUpdate?: () => void;
}

const StudentDetailModal: React.FC<StudentDetailModalProps> = ({ isOpen, onClose, studentId, onUpdate }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [student, setStudent] = useState<any>(null);
    const [showLeadDetail, setShowLeadDetail] = useState(false);
    const [selectedLead, setSelectedLead] = useState<any>(null);

    // State for the specific sections
    const [interestedDepts, setInterestedDepts] = useState<string[]>([]);
    const [examResults, setExamResults] = useState<ExamResult[]>([]);
    const [languageDocs, setLanguageDocs] = useState<LanguageDoc[]>([]);
    const [applications, setApplications] = useState<SchoolApplication[]>([]);
    const [universitaly, setUniversitaly] = useState<UniversitalyInfo>({
        id: '',
        status: 'pending'
    });
    const [documents, setDocuments] = useState<any[]>([]);

    // University State
    const [universities, setUniversities] = useState<any[]>([]);

    // Personality Tests State
    const [ptTests, setPtTests] = useState<PersonalityTest[]>([]);
    const [ptUserAccount, setPtUserAccount] = useState<PTUserAccount | null>(null);
    const [assignedTests, setAssignedTests] = useState<AssignedTest[]>([]);
    const [testResults, setTestResults] = useState<TestResultData[]>([]);
    const [newPtEmail, setNewPtEmail] = useState('');
    const [newPtPassword, setNewPtPassword] = useState('');
    const [selectedTests, setSelectedTests] = useState<string[]>([]);
    const [ptLoading, setPtLoading] = useState(false);
    const [upcomingDeadlines, setUpcomingDeadlines] = useState<any[]>([]);

    // Document editing state
    const [editingDocId, setEditingDocId] = useState<string | null>(null);
    const [editingDocName, setEditingDocName] = useState('');


    useEffect(() => {
        if (isOpen && studentId) {
            fetchStudentDetails();
            fetchUniversities();
            fetchPtTests();
        }
    }, [isOpen, studentId]);


    const fetchUniversities = async () => {
        const { data } = await supabase.from('universities').select('id, name').order('name');
        setUniversities(data || []);
    };

    // Fetch all available personality tests
    const fetchPtTests = async () => {
        const { data } = await supabase.from('pt_tests').select('*').order('title');
        setPtTests(data || []);
    };

    // Check if student has a PT account linked (by pt_user_id or email)
    const fetchPtUserAccount = async (ptUserIdOrEmail: string, isEmail: boolean = true) => {
        if (!ptUserIdOrEmail) return;

        let query = supabase.from('pt_users').select('id, email, full_name');

        if (isEmail) {
            query = query.eq('email', ptUserIdOrEmail);
        } else {
            query = query.eq('id', ptUserIdOrEmail);
        }

        const { data } = await query.single();
        setPtUserAccount(data || null);
        if (data) {
            fetchAssignedTests(data.id);
        }
    };

    // Fetch assigned tests for PT user
    const fetchAssignedTests = async (ptUserId: string) => {
        const { data } = await supabase
            .from('pt_student_tests')
            .select(`
                id,
                test_id,
                is_completed,
                assigned_at,
                completed_at,
                pt_tests(title)
            `)
            .eq('student_id', ptUserId)
            .order('assigned_at', { ascending: false });

        const mapped = (data || []).map((item: any) => ({
            id: item.id,
            test_id: item.test_id,
            test_title: item.pt_tests?.title || 'Bilinmeyen Test',
            is_completed: item.is_completed,
            assigned_at: item.assigned_at,
            completed_at: item.completed_at
        }));
        setAssignedTests(mapped);

        // Fetch results for completed tests
        const completedIds = mapped.filter((t: AssignedTest) => t.is_completed).map((t: AssignedTest) => t.id);
        if (completedIds.length > 0) {
            const { data: results } = await supabase
                .from('pt_test_results')
                .select('*')
                .in('student_test_id', completedIds);
            setTestResults(results || []);
        }
    };

    // Create PT user account and link to operation_students
    const createPtUserAccount = async () => {
        if (!newPtEmail || !newPtPassword) {
            alert('Email ve şifre zorunludur!');
            return;
        }
        setPtLoading(true);
        try {
            // Store plain text password for PT site login
            const { data, error } = await supabase
                .from('pt_users')
                .insert({
                    email: newPtEmail,
                    password_hash: newPtPassword,
                    password_plain: newPtPassword,
                    full_name: `${student?.first_name || ''} ${student?.last_name || ''}`.trim(),
                    role: 'student'
                })
                .select()
                .single();

            if (error) throw error;

            // Link PT user to operation_students
            if (data && studentId) {
                await supabase
                    .from('operation_students')
                    .update({ pt_user_id: data.id })
                    .eq('id', studentId);

                // Update local student state with pt_user_id
                setStudent((prev: any) => ({ ...prev, pt_user_id: data.id }));
            }

            setPtUserAccount(data);
            setNewPtEmail('');
            setNewPtPassword('');
            alert('Kişilik testi hesabı oluşturuldu!');
        } catch (err: any) {
            console.error('PT User Creation Error:', err);
            alert('Hesap oluşturulamadı: ' + (err.message || 'Bilinmeyen hata'));
        } finally {
            setPtLoading(false);
        }
    };

    // Assign selected tests to PT user
    const assignTestsToUser = async () => {
        if (!ptUserAccount || selectedTests.length === 0) {
            alert('Lütfen en az bir test seçin!');
            return;
        }
        setPtLoading(true);
        try {
            const insertData = selectedTests.map(testId => ({
                student_id: ptUserAccount.id,
                test_id: testId,
                is_completed: false
            }));

            const { error } = await supabase
                .from('pt_student_tests')
                .upsert(insertData, { onConflict: 'student_id,test_id' });

            if (error) throw error;

            // Get test names for SMS
            const selectedTestNames = ptTests
                .filter(t => selectedTests.includes(t.id))
                .map(t => t.title)
                .join(', ');

            // Send SMS notification to student
            const studentPhone = student?.phone;
            let smsSent = false;

            if (studentPhone && studentPhone.trim() !== '') {
                // Fetch password from pt_users for SMS
                const { data: ptUserData } = await supabase
                    .from('pt_users')
                    .select('email, password_plain')
                    .eq('id', ptUserAccount.id)
                    .single();

                const userEmail = ptUserData?.email || ptUserAccount.email;
                const userPassword = ptUserData?.password_plain || '(Sifrenizi olusturdugunuzda belirlediginiz sifre)';

                const smsMessage = `PISA Kisilik Testi Bilgilendirmesi

Sayin ${student?.first_name || ''} ${student?.last_name || ''},

Size atanan testler: ${selectedTestNames}

Giris Bilgileriniz:
Email: ${userEmail}
Sifre: ${userPassword}
Site: https://pisapt.netlify.app

Testlerinizi tamamlamak icin yukaridaki siteye giris yapiniz.

PISA Egitim`;

                try {
                    const { data: userData } = await supabase.auth.getUser();
                    const { error: smsError } = await supabase.functions.invoke('send-sms', {
                        body: {
                            phones: [studentPhone],
                            message: smsMessage,
                            userId: userData?.user?.id || '',
                            branchId: ''
                        }
                    });

                    if (!smsError) {
                        smsSent = true;
                        console.log('SMS sent successfully for PT tests to:', studentPhone);
                    } else {
                        console.error('SMS API error:', smsError);
                    }
                } catch (smsError) {
                    console.error('SMS sending failed:', smsError);

                }
            } else {
                console.warn('No phone number available for SMS');
            }

            setSelectedTests([]);
            fetchAssignedTests(ptUserAccount.id);

            if (smsSent) {
                alert('Testler başarıyla atandı ve SMS gönderildi!');
            } else if (!studentPhone || studentPhone.trim() === '') {
                alert('Testler başarıyla atandı.\n\n⚠️ SMS gönderilemedi: Telefon numarası bulunamadı. Lütfen önce iletişim bilgilerini kaydedin.');
            } else {
                alert('Testler başarıyla atandı.\n\n⚠️ SMS gönderiminde bir sorun oluştu.');
            }
        } catch (err: any) {
            console.error('Test Assignment Error:', err);
            alert('Test atanamadı: ' + (err.message || 'Bilinmeyen hata'));

        } finally {
            setPtLoading(false);
        }
    };


    const fetchStudentDetails = async () => {
        try {
            setLoading(true);

            // 1. Try Fetching from 'students' (Regular)
            let { data: studentData, error: studentError } = await supabase
                .from('students')
                .select(`
          *,
          leads (*)
        `)
                .eq('id', studentId)
                .maybeSingle();

            // If not found in 'students', try 'operation_students'
            if (!studentData) {
                const { data: opData, error: opError } = await supabase
                    .from('operation_students')
                    .select('*')
                    .eq('id', studentId)
                    .maybeSingle();

                if (opData) {
                    // Parse 'notes' to extract details
                    const parsedData = parseOperationNotes(opData.notes);

                    setStudent({
                        ...opData,
                        nationality: 'Türkiye' // Default for now
                    });

                    setInterestedDepts(parsedData.departments);
                    setExamResults(parsedData.exams);

                    // Match parsed school names to university IDs
                    const { data: allUnis } = await supabase.from('universities').select('id, name');
                    const enhancedApps = parsedData.applications.map(app => {
                        const match = allUnis?.find(u => u.name.toLowerCase() === app.school_name.toLowerCase());
                        return { ...app, university_id: match?.id };
                    });

                    setApplications(enhancedApps);
                    setLanguageDocs(parsedData.languages || []);
                    if (parsedData.universitaly) {
                        setUniversitaly(parsedData.universitaly);
                    }
                    // IMPORTANT: Set documents from parsed notes
                    setDocuments(parsedData.documents || []);

                    // Check for PT account - prioritize pt_user_id, fallback to email
                    if (opData.pt_user_id) {
                        fetchPtUserAccount(opData.pt_user_id, false);
                    } else if (opData.email) {
                        fetchPtUserAccount(opData.email, true);
                    }

                    // FETCH DEADLINES FOR OPERATION STUDENTS TOO
                    fetchDeadlines(studentId);
                }

            } else {
                // Regular student found
                setStudent(studentData);
                if (studentData.leads && studentData.leads[0]) {
                    setInterestedDepts(studentData.leads[0].interested_programs || []);
                }

                // Fetch real sub-data for regular students
                fetchDocuments(studentId);
                fetchDeadlines(studentId);

                // Check for PT account
                if (studentData.email) {
                    fetchPtUserAccount(studentData.email);
                }

                if (studentData.leads && studentData.leads.length > 0) {
                    setSelectedLead(studentData.leads[0]);
                }
            }


        } catch (error) {
            console.error('Error fetching student details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetches documents (Only for Regular Students)
    const fetchDocuments = async (id: string) => {
        const { data: docs } = await supabase
            .from('student_documents')
            .select('*')
            .eq('student_id', id)
            .order('uploaded_at', { ascending: false });
        setDocuments(docs || []);
    };

    // Fetches deadlines (For ALL Students)
    const fetchDeadlines = async (id: string) => {
        const { data: deadlines } = await supabase
            .from('student_exam_deadlines')
            .select('*')
            .eq('student_id', id)
            .order('due_date', { ascending: true });
        setUpcomingDeadlines(deadlines || []);
    };

    const parseOperationNotes = (notes: string | null) => {
        if (!notes) return { departments: [], exams: [], languages: [], applications: [], universitaly: null, documents: [] };

        const result = {
            departments: [] as string[],
            exams: [] as any[],
            languages: [] as any[],
            applications: [] as any[],
            universitaly: null as any,
            documents: [] as any[]
        };

        try {
            // Helper to extract content between headers
            const extractSection = (text: string, header: string, nextHeaders: string[]): string => {
                const headerIndex = text.indexOf(header);
                if (headerIndex === -1) return '';

                let content = text.slice(headerIndex + header.length);
                let minIndex = content.length;

                nextHeaders.forEach(next => {
                    const idx = content.indexOf(next);
                    if (idx !== -1 && idx < minIndex) {
                        minIndex = idx;
                    }
                });

                return content.slice(0, minIndex).trim();
            };

            const HEADERS = {
                DETAILS: '=== DETAYLAR ===',
                EXAMS: '--- SINAVLAR ---',
                LANGS: '--- DİL BELGELERİ ---',
                APPS: '--- BAŞVURULAR ---',
                UNI: '--- UNIVERSITALY ---',
                DOCS: '--- DOSYALAR ---'
            };

            // 1. Departments
            const detailsContent = extractSection(notes, HEADERS.DETAILS, [HEADERS.EXAMS, HEADERS.LANGS, HEADERS.APPS, HEADERS.UNI, HEADERS.DOCS]);
            const deptMatch = detailsContent.match(/Bölümler:\s*(.*)/);
            if (deptMatch) {
                result.departments = deptMatch[1].split(',').map(s => s.trim()).filter(Boolean);
            }

            // 2. Exams
            const examsContent = extractSection(notes, HEADERS.EXAMS, [HEADERS.LANGS, HEADERS.APPS, HEADERS.UNI, HEADERS.DOCS]);
            if (examsContent) {
                result.exams = examsContent.split('\n').filter(Boolean).map(line => {
                    const parts = line.match(/^([\d.-]+)?\s*(.*?):\s*([\d.]+)\s*(\((.*)\))?/);
                    if (parts) {
                        return {
                            id: Math.random().toString(),
                            exam_date: parts[1] || '',
                            exam_name: parts[2]?.trim(),
                            score: parts[3],
                            note: parts[5]
                        };
                    }
                    return null;
                }).filter(Boolean);
            }

            // 3. Languages
            const langsContent = extractSection(notes, HEADERS.LANGS, [HEADERS.APPS, HEADERS.UNI, HEADERS.DOCS]);
            if (langsContent) {
                result.languages = langsContent.split('\n').filter(Boolean).map(line => {
                    const parts = line.match(/^(.*)\s*\((.*)\):\s*(.*)/);
                    if (parts) {
                        return {
                            id: Math.random().toString(),
                            name: parts[1]?.trim(),
                            level: parts[2],
                            note: parts[3]
                        };
                    }
                    return null;
                }).filter(Boolean);
            }

            // 4. Applications
            const appsContent = extractSection(notes, HEADERS.APPS, [HEADERS.UNI, HEADERS.DOCS]);
            if (appsContent) {
                result.applications = appsContent.split('\n').filter(Boolean).map(line => {
                    const parts = line.split(' - ');
                    const statusText = parts[1]?.match(/\((.*)\)/)?.[1] || 'Bekliyor';
                    let status: any = 'pending';

                    const lowerStatus = statusText.toLowerCase();
                    if (lowerStatus.includes('tamamlandı')) status = 'completed';
                    else if (lowerStatus.includes('kabul')) status = 'accepted';
                    else if (lowerStatus.includes('red')) status = 'rejected';
                    else if (lowerStatus.includes('başvuru yapıldı')) status = 'application_made';
                    else if (lowerStatus.includes('kayıt oldu')) status = 'enrolled';

                    return {
                        id: Math.random().toString(),
                        school_name: parts[0]?.trim(),
                        department: parts[1]?.split('(')[0]?.trim(),
                        result_status: statusText,
                        status: status
                    };
                });
            }

            // 5. Universitaly
            const uniContent = extractSection(notes, HEADERS.UNI, [HEADERS.DOCS]);
            if (uniContent && uniContent.trim() !== 'Yok' && (uniContent.trim().startsWith('{') || uniContent.trim().startsWith('['))) {
                try {
                    result.universitaly = JSON.parse(uniContent);
                } catch (e) {
                    // Silent fail - content is not valid JSON
                }
            }

            // 6. Documents
            const docsContent = extractSection(notes, HEADERS.DOCS, []);
            if (docsContent) {
                try {
                    const parsed = JSON.parse(docsContent);
                    if (Array.isArray(parsed)) {
                        result.documents = parsed;
                    }
                } catch (e) {
                    console.error('JSON Parse Error (Documents):', e);
                }
            }

        } catch (error) {
            console.error('Parse Operation Notes Error:', error);
        }

        return result;
    };

    const handleSave = async (silent: boolean = false, appsOverride?: SchoolApplication[]) => {
        if (!student) return;

        try {
            // Sync Applications to university_applications table
            const appsToSave = appsOverride || applications;
            for (const app of appsToSave) {
                // Determine status label for DB
                let appStatus = 'Başvuru Yapıldı';
                if (app.status === 'completed') appStatus = 'TAMAMLANDI';
                if (app.status === 'accepted') appStatus = 'Kabul';
                if (app.status === 'rejected') appStatus = 'Red';
                if (app.status === 'enrolled') appStatus = 'Kayıt Oldu';

                // Only sync if selected status is relevant or we want to track all
                if (['application_made', 'enrolled', 'completed', 'accepted', 'rejected', 'pending'].includes(app.status)) {
                    // Try to find university ID if not set
                    let uniId = (app as any).university_id;
                    if (!uniId) {
                        const match = universities.find(u => u.name.toLowerCase() === app.school_name.toLowerCase());
                        uniId = match?.id;
                    }

                    if (uniId) {
                        // Check if exists
                        const { data: existing } = await supabase
                            .from('university_applications')
                            .select('id')
                            .eq('student_id', studentId)
                            .eq('university_id', uniId)
                            .maybeSingle();

                        const payload = {
                            university_id: uniId,
                            student_id: studentId,
                            student_name: student.first_name,
                            student_surname: student.last_name,
                            department: app.department,
                            username: app.username,
                            password: app.password,
                            application_status: appStatus,
                            result: app.result_status,
                            updated_at: new Date().toISOString()
                        };

                        if (existing) {
                            await supabase.from('university_applications').update(payload).eq('id', existing.id);
                        } else {
                            await supabase.from('university_applications').insert(payload);
                        }
                    }
                }
            }

            // If Operation Student (has notes field)
            if (student.notes !== undefined) {
                const newNotes = `
=== DETAYLAR ===
Bölümler: ${interestedDepts.join(', ')}

--- SINAVLAR ---
${examResults.map(e => `${e.exam_date || ''} ${e.exam_name}: ${e.score} (${e.note || ''})`).join('\n')}

--- DİL BELGELERİ ---
${languageDocs.map(d => `${d.name} (${d.level}): ${d.note || ''}`).join('\n')}

--- BAŞVURULAR ---
${(appsOverride || applications).map(a => `${a.school_name} - ${a.department} (${a.result_status || a.status})`).join('\n')}

--- UNIVERSITALY ---
${universitaly && universitaly.username ? JSON.stringify(universitaly) : 'Yok'}

--- DOSYALAR ---
${documents.length > 0 ? JSON.stringify(documents) : '[]'}
`.trim();

                const { error } = await supabase
                    .from('operation_students')
                    .update({
                        notes: newNotes,
                        email: student.email || null,
                        phone: student.phone || null
                    })
                    .eq('id', studentId);

                if (error) throw error;

                if (!silent) {
                    alert('Değişiklikler kaydedildi!');
                    onCloseModal(); // Defined below
                }
                if (onUpdate) onUpdate();

            } else {
                if (!silent) alert('Normal öğrenciler için düzenleme henüz aktif değil.');
            }

        } catch (error: any) {
            console.error('Save Error:', error);
            alert('Kaydetme hatası: ' + error.message);
        }
    };

    const onCloseModal = () => {
        onClose();
        setStudent(null);
    }

    // ... rest of component


    // Modal States
    const [activeModal, setActiveModal] = useState<'none' | 'exam' | 'lang' | 'app' | 'dept' | 'deadline'>('none');
    const [tempItem, setTempItem] = useState<any>({});

    const openModal = (type: 'exam' | 'lang' | 'app' | 'dept' | 'deadline') => {
        setTempItem({});
        setActiveModal(type);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, category: string) => {
        try {
            const file = event.target.files?.[0];
            if (!file) return;

            setLoading(true);
            const user = (await supabase.auth.getUser()).data.user;
            if (!user) throw new Error('Kullanıcı bulunamadı');

            // Upload to Storage
            const fileExt = file.name.split('.').pop();
            const fileName = `${category}_${Date.now()}.${fileExt}`;
            const filePath = `${studentId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('student-documents')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            // Get Public URL
            const { data: { publicUrl } } = supabase.storage
                .from('student-documents')
                .getPublicUrl(filePath);

            // Handle based on Student Type
            if (student.notes !== undefined) {
                // Operation Student: Update Local State
                const newDoc = {
                    id: Math.random().toString(),
                    file_name: file.name,
                    file_path: filePath,
                    public_url: publicUrl,
                    uploaded_at: new Date().toISOString(),
                    file_size: file.size,
                    document_type: category
                };
                setDocuments([newDoc, ...documents]);
                alert('Dosya yüklendi! Kaydetmeyi unutmayın.');
            } else {
                // Regular Student: Insert into DB
                const { error: dbError } = await supabase
                    .from('student_documents')
                    .insert({
                        student_id: studentId,
                        document_type: category,
                        file_name: file.name,
                        file_path: filePath,
                        file_size: file.size,
                        mime_type: file.type,
                        uploaded_by: user.id
                    });

                if (dbError) throw dbError;
                fetchDocuments(studentId); // Refresh list
                alert('Dosya başarıyla yüklendi.');
            }

        } catch (error: any) {
            console.error('Upload Error:', error);
            alert('Dosya yükleme hatası: ' + error.message);
        } finally {
            setLoading(false);
            // Reset input
            if (event.target) event.target.value = '';
        }
    };

    const handleRenameDocument = (doc: any) => {
        setEditingDocId(doc.id);
        setEditingDocName(doc.file_name || doc.name || '');
    };

    const handleSaveDocName = async (doc: any) => {
        if (!editingDocName.trim()) {
            alert('Dosya adı boş olamaz');
            return;
        }

        try {
            if (student.notes !== undefined) {
                // Operation Student: Update local state
                setDocuments(documents.map(d =>
                    d.id === doc.id ? { ...d, file_name: editingDocName } : d
                ));
            } else {
                // Regular Student: Update in DB
                const { error } = await supabase
                    .from('student_documents')
                    .update({ file_name: editingDocName })
                    .eq('id', doc.id);

                if (error) throw error;
                fetchDocuments(studentId);
            }
        } catch (error: any) {
            console.error('Rename Error:', error);
            alert('İsim değiştirme hatası: ' + error.message);
        } finally {
            setEditingDocId(null);
            setEditingDocName('');
        }
    };

    const saveDeadline = async () => {
        try {
            const user = (await supabase.auth.getUser()).data.user;
            if (!user) throw new Error('Kullanıcı bulunamadı');

            // Convert datetime-local value (which is naive) to a proper Date object
            // datetime-local format is YYYY-MM-DDTHH:mm
            const localDate = new Date(tempItem.due_date);

            const { data, error } = await supabase
                .from('student_exam_deadlines')
                .insert({
                    title: tempItem.title,
                    description: tempItem.description || '',
                    due_date: localDate.toISOString(), // .toISOString() converts the local Date object to UTC Z
                    type: tempItem.type || 'exam',
                    student_id: studentId,
                    created_by: user.id,
                    reminder_days: [3],
                })
                .select()
                .single();

            if (error) throw error;

            setUpcomingDeadlines([...upcomingDeadlines, data]);
            setActiveModal('none');
            setTempItem({});
        } catch (error: any) {
            console.error('Error saving deadline:', error);
            alert('Tarih kaydedilemedi: ' + error.message);
        }
    };
    const handleModalSave = () => {
        if (activeModal === 'exam') {
            if (!tempItem.exam_name) return alert('Sınav adı zorunludur');
            setExamResults([...examResults, {
                id: Math.random().toString(),
                exam_name: tempItem.exam_name,
                score: tempItem.score || '',
                exam_date: tempItem.exam_date || format(new Date(), 'yyyy-MM-dd'),
                note: tempItem.note || ''
            }]);
        } else if (activeModal === 'lang') {
            if (!tempItem.name) return alert('Belge adı zorunludur');
            setLanguageDocs([...languageDocs, {
                id: Math.random().toString(),
                name: tempItem.name,
                level: tempItem.level || '',
                note: tempItem.note || ''
            }]);
        } else if (activeModal === 'app') {
            if (!tempItem.school_name) return alert('Okul adı zorunludur');

            const uniMatch = universities.find(u => u.name.toLowerCase() === tempItem.school_name.toLowerCase());

            setApplications([...applications, {
                id: Math.random().toString(),
                school_name: tempItem.school_name,
                department: tempItem.department || '',
                status: 'pending',
                payment_status: '-',
                result_status: 'Bekliyor',
                university_id: uniMatch?.id
            }]);
        } else if (activeModal === 'deadline') {
            if (!tempItem.title) return alert('Başlık zorunludur');
            if (!tempItem.due_date) return alert('Tarih zorunludur');

            saveDeadline();
        } else if (activeModal === 'dept') {
            if (!tempItem.dept_name) return alert('Bölüm adı zorunludur');
            setInterestedDepts([...interestedDepts, tempItem.dept_name]);
        }
        setActiveModal('none');
        setTempItem({});
    };

    const handleDownload = async (doc: any) => {
        try {
            if (!doc.file_path) {
                if (doc.public_url) window.open(doc.public_url, '_blank');
                return;
            }

            const { data, error } = await supabase.storage
                .from('student-documents')
                .download(doc.file_path);

            if (error) throw error;

            const url = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.file_name || 'download';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Download Error:', error);
            alert('Dosya indirilemedi: ' + error.message);
        }
    };

    const handleDeleteDocument = async (docToDelete: any) => {
        if (!await window.appConfirm('Bu belgeyi silmek istediğinize emin misiniz?')) return;

        try {
            // Operation Student
            if (student.notes !== undefined) {
                const updatedDocs = documents.filter(d => d.id !== docToDelete.id);
                setDocuments(updatedDocs);
                // We don't delete from storage yet because user might not save details. 
                // It will just be removed from the list reference.
            } else {
                // Regular Student
                setLoading(true);

                // 1. Delete from DB
                const { error: dbError } = await supabase
                    .from('student_documents')
                    .delete()
                    .eq('id', docToDelete.id);

                if (dbError) throw dbError;

                // 2. Delete from Storage (Optional but good for cleanup)
                if (docToDelete.file_path) {
                    await supabase.storage
                        .from('student-documents')
                        .remove([docToDelete.file_path]);
                }

                // 3. Update UI
                setDocuments(documents.filter(d => d.id !== docToDelete.id));
                alert('Belge silindi.');
            }
        } catch (error: any) {
            console.error('Delete Error:', error);
            alert('Silme hatası: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 w-full max-w-[98vw] max-h-[95vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-800">

                {/* Header - Always Visible */}
                <div className="px-8 py-6 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex justify-between items-start z-10">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 text-white text-2xl font-bold">
                            {student?.first_name?.[0]}{student?.last_name?.[0]}
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {student?.first_name} {student?.last_name}
                                </h2>
                                <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 text-xs font-bold uppercase tracking-wider">
                                    {student?.education_level || 'Öğrenci'} {student?.academic_year || ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 font-medium">
                                <span className="flex items-center gap-1.5">
                                    <User className="w-4 h-4" />
                                    {student?.id?.slice(0, 8)}
                                </span>
                                <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
                                <span className="flex items-center gap-1.5">
                                    <Globe className="w-4 h-4" />
                                    {student?.nationality || 'Türkiye'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {user?.role === 'admin' && selectedLead && (
                            <button
                                onClick={() => setShowLeadDetail(true)}
                                className="px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 rounded-lg text-sm font-bold transition-colors flex items-center gap-2"
                            >
                                <FileText className="w-4 h-4" />
                                Satış Detayları
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2.5 rounded-full bg-gray-50 dark:bg-gray-800 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content Scrollable Area */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-black/20 p-8">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                        </div>
                    ) : (
                        <div className="space-y-8">

                            {/* Top Grid: Departments & Exams & Language */}
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                                {/* Left Column: Interests & Exams */}
                                <div className="lg:col-span-3 space-y-6">

                                    {/* General Info Section */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-l-blue-500">
                                        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                                            <User className="w-5 h-5 text-blue-500" />
                                            Genel Bilgiler
                                        </h3>
                                        <div className="space-y-3">
                                            {/* Email */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl">
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">E-posta</label>
                                                <input
                                                    type="email"
                                                    value={student?.email || ''}
                                                    onChange={(e) => setStudent({ ...student, email: e.target.value })}
                                                    placeholder="E-posta giriniz"
                                                    className="w-full bg-transparent border-none p-0 text-sm text-gray-900 dark:text-white font-medium focus:ring-0 placeholder-gray-300"
                                                />
                                            </div>
                                            {/* Phone */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl">
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">Telefon</label>
                                                <input
                                                    type="tel"
                                                    value={student?.phone || ''}
                                                    onChange={(e) => setStudent({ ...student, phone: e.target.value })}
                                                    placeholder="Telefon giriniz"
                                                    className="w-full bg-transparent border-none p-0 text-sm text-gray-900 dark:text-white font-medium focus:ring-0 placeholder-gray-300"
                                                />
                                            </div>
                                            {/* Education Level */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl">
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">Eğitim Durumu</label>
                                                <select
                                                    value={student?.education_level || ''}
                                                    onChange={(e) => setStudent({ ...student, education_level: e.target.value })}
                                                    className="w-full bg-transparent border-none p-0 text-sm text-gray-900 dark:text-white font-medium focus:ring-0 cursor-pointer"
                                                >
                                                    <option value="">Seçiniz</option>
                                                    <option value="12. Sınıf">12. Sınıf</option>
                                                    <option value="Mezun">Mezun</option>
                                                    <option value="Üniversite Öğrencisi">Üniversite Öğrencisi</option>
                                                    <option value="Lisans Mezunu">Lisans Mezunu</option>
                                                    <option value="Yüksek Lisans">Yüksek Lisans</option>
                                                </select>
                                            </div>
                                            {/* Academic Year */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl">
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">Akademik Yıl</label>
                                                <select
                                                    value={student?.academic_year || ''}
                                                    onChange={(e) => setStudent({ ...student, academic_year: e.target.value })}
                                                    className="w-full bg-transparent border-none p-0 text-sm text-gray-900 dark:text-white font-medium focus:ring-0 cursor-pointer"
                                                >
                                                    <option value="">Seçiniz</option>
                                                    <option value="2025">2025</option>
                                                    <option value="2026">2026</option>
                                                    <option value="2027">2027</option>
                                                    <option value="2028">2028</option>
                                                </select>
                                            </div>
                                            {/* Status */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl">
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">Durum</label>
                                                <select
                                                    value={student?.status || ''}
                                                    onChange={(e) => setStudent({ ...student, status: e.target.value })}
                                                    className="w-full bg-transparent border-none p-0 text-sm text-gray-900 dark:text-white font-medium focus:ring-0 cursor-pointer"
                                                >
                                                    <option value="">Seçiniz</option>
                                                    <option value="Ön Kayıt Aşamasında">Ön Kayıt Aşamasında</option>
                                                    <option value="Kayıt Tamamlandı">Kayıt Tamamlandı</option>
                                                    <option value="Başvuru Aşamasında">Başvuru Aşamasında</option>
                                                    <option value="Kabul Aldı">Kabul Aldı</option>
                                                    <option value="Vize Sürecinde">Vize Sürecinde</option>
                                                    <option value="Tamamlandı">Tamamlandı</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Interested Departments */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <BookOpen className="w-5 h-5 text-rose-500" />
                                                İlgilendiği Bölümler
                                            </h3>
                                            <button
                                                onClick={() => openModal('dept')}
                                                className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 px-3 py-1.5 rounded-lg transition-colors">
                                                + Ekle
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {interestedDepts.length > 0 ? interestedDepts.map((dept, i) => (
                                                <span key={i} className="px-3 py-1.5 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 text-sm font-semibold border border-rose-100 dark:border-rose-800/50">
                                                    {dept}
                                                </span>
                                            )) : (
                                                <p className="text-sm text-gray-400 italic">Bölüm tercihi girilmemiş.</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Exam & Application Deadlines Section */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-l-rose-500">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <Calendar className="w-5 h-5 text-rose-500" />
                                                Sınav ve Başvuru Tarihleri
                                            </h3>
                                            <button
                                                onClick={() => openModal('deadline')}
                                                className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 px-3 py-1.5 rounded-lg transition-colors">
                                                + Tarih Ekle
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {upcomingDeadlines.length > 0 ? (
                                                upcomingDeadlines.map(deadline => (
                                                    <div key={deadline.id} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 group relative">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="font-bold text-gray-900 dark:text-white">{deadline.title}</span>
                                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${deadline.type === 'exam' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                                                                        }`}>
                                                                        {deadline.type === 'exam' ? 'SINAV' : 'BAŞVURU'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-2 text-sm text-rose-600 font-medium">
                                                                    <Calendar className="w-4 h-4" />
                                                                    {format(new Date(deadline.due_date), 'dd.MM.yyyy')}
                                                                    <span className="text-gray-400 text-xs font-normal">
                                                                        ({format(new Date(deadline.due_date), 'HH:mm')})
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-2">
                                                                <div className="flex items-center gap-1 text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-bold">
                                                                    <Globe className="w-3 h-3" />
                                                                    3 GÜN ÖNCE SMS
                                                                </div>
                                                                <button
                                                                    onClick={async () => {
                                                                        if (await window.appConfirm('Bu tarihi silmek istiyor musunuz?')) {
                                                                            await supabase.from('student_exam_deadlines').delete().eq('id', deadline.id);
                                                                            setUpcomingDeadlines(upcomingDeadlines.filter(d => d.id !== deadline.id));
                                                                        }
                                                                    }}
                                                                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {deadline.description && (
                                                            <div className="mt-2 text-xs text-gray-500 italic">
                                                                {deadline.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-6 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                                                    <p className="text-xs text-gray-400">Henüz yaklaşan bir sınav veya başvuru tarihi eklenmemiş.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <Award className="w-5 h-5 text-emerald-500" />
                                                Sınav Sonuçları
                                            </h3>
                                            <button
                                                onClick={() => openModal('exam')}
                                                className="text-xs font-bold text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg transition-colors">
                                                + Ekle
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {examResults.map(exam => (
                                                <div key={exam.id} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <span className="font-bold text-gray-900 dark:text-white">{exam.exam_name}</span>
                                                        <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold">
                                                            {exam.score}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                        <Calendar className="w-3 h-3" />
                                                        {exam.exam_date}
                                                    </div>
                                                    {exam.note && (
                                                        <div className="mt-2 text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded">
                                                            Not: {exam.note}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Language Certificates */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <Globe className="w-5 h-5 text-amber-500" />
                                                Dil Belgeleri
                                            </h3>
                                            <button
                                                onClick={() => openModal('lang')}
                                                className="text-xs font-bold text-amber-600 hover:text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg transition-colors">
                                                + Ekle
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {languageDocs.map(lang => (
                                                <div key={lang.id} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <span className="font-bold text-gray-900 dark:text-white">{lang.name}</span>
                                                        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">{lang.level}</span>
                                                    </div>
                                                    {lang.note && (
                                                        <div className="mt-2 text-xs text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                                                            Not: {lang.note}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Contact Information Section */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-l-blue-500">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <User className="w-5 h-5 text-blue-500" />
                                                İletişim Bilgileri
                                            </h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 mb-1 block">Email</label>
                                                <input
                                                    type="email"
                                                    value={student?.email || ''}
                                                    onChange={(e) => setStudent({ ...student, email: e.target.value })}
                                                    placeholder="ornek@email.com"
                                                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 mb-1 block">Telefon</label>
                                                <input
                                                    type="tel"
                                                    value={student?.phone || ''}
                                                    onChange={(e) => setStudent({ ...student, phone: e.target.value })}
                                                    placeholder="+90 5XX XXX XX XX"
                                                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleSave(false)}
                                                className="w-full mt-2 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold transition-colors"
                                            >
                                                İletişim Bilgilerini Kaydet
                                            </button>
                                        </div>
                                    </div>


                                    {/* Personality Tests Section */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-l-purple-500">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <Award className="w-5 h-5 text-purple-500" />
                                                Kişilik Testleri
                                            </h3>
                                        </div>

                                        {/* PT Account Status */}
                                        {!ptUserAccount ? (
                                            <div className="space-y-4">
                                                <p className="text-sm text-gray-500">Bu öğrenci için henüz kişilik testi hesabı oluşturulmamış.</p>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-500 mb-1 block">Email</label>
                                                        <input
                                                            type="email"
                                                            value={newPtEmail}
                                                            onChange={(e) => setNewPtEmail(e.target.value)}
                                                            placeholder="ornek@email.com"
                                                            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-500 mb-1 block">Şifre</label>
                                                        <input
                                                            type="text"
                                                            value={newPtPassword}
                                                            onChange={(e) => setNewPtPassword(e.target.value)}
                                                            placeholder="Şifre belirleyin"
                                                            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                        />
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={createPtUserAccount}
                                                    disabled={ptLoading}
                                                    className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                                                >
                                                    {ptLoading ? 'Oluşturuluyor...' : 'Hesap Oluştur'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {/* Account Info */}
                                                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                                        Hesap: {ptUserAccount.email}
                                                    </p>
                                                </div>

                                                {/* Test Selection */}
                                                <div>
                                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Test Seç ve Gönder</label>
                                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                                        {ptTests.map(test => (
                                                            <label key={test.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedTests.includes(test.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedTests([...selectedTests, test.id]);
                                                                        } else {
                                                                            setSelectedTests(selectedTests.filter(id => id !== test.id));
                                                                        }
                                                                    }}
                                                                    className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                                                                />
                                                                <div>
                                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{test.title}</span>
                                                                    {test.description && (
                                                                        <p className="text-xs text-gray-500">{test.description}</p>
                                                                    )}
                                                                </div>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {selectedTests.length > 0 && (
                                                        <button
                                                            onClick={assignTestsToUser}
                                                            disabled={ptLoading}
                                                            className="mt-3 w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                                                        >
                                                            {ptLoading ? 'Gönderiliyor...' : `${selectedTests.length} Test Gönder`}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Assigned Tests & Results */}
                                                {assignedTests.length > 0 && (
                                                    <div>
                                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Atanan Testler</label>
                                                        <div className="space-y-2">
                                                            {assignedTests.map(test => {
                                                                const result = testResults.find(r => r.student_test_id === test.id);
                                                                return (
                                                                    <div key={test.id} className={`p-3 rounded-lg border ${test.is_completed ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'}`}>
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{test.test_title}</span>
                                                                            <span className={`text-xs font-bold px-2 py-1 rounded ${test.is_completed ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                                {test.is_completed ? 'Tamamlandı' : 'Bekliyor'}
                                                                            </span>
                                                                        </div>
                                                                        {result && (
                                                                            <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg border text-xs space-y-3">
                                                                                <strong className="text-gray-700 dark:text-gray-300">Sonuç:</strong>
                                                                                {/* Ranking Display */}
                                                                                {result.result_data?.ranking && Array.isArray(result.result_data.ranking) && (
                                                                                    <div className="space-y-2">
                                                                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sıralama</p>
                                                                                        <div className="space-y-1.5">
                                                                                            {result.result_data.ranking.slice(0, 5).map((item: any, idx: number) => (
                                                                                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                                                                                    <div className="flex items-center gap-2">
                                                                                                        <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                                                                            idx === 1 ? 'bg-gray-100 text-gray-600' :
                                                                                                                idx === 2 ? 'bg-orange-100 text-orange-700' :
                                                                                                                    'bg-gray-50 text-gray-500'
                                                                                                            }`}>
                                                                                                            {idx + 1}
                                                                                                        </span>
                                                                                                        <span className="font-medium text-gray-700 dark:text-gray-300">{item.category}</span>
                                                                                                    </div>
                                                                                                    <div className="flex items-center gap-2">
                                                                                                        <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                                                            <div
                                                                                                                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                                                                                                                style={{ width: `${(item.score / 10) * 100}%` }}
                                                                                                            />
                                                                                                        </div>
                                                                                                        <span className="font-bold text-purple-600 dark:text-purple-400 min-w-[40px] text-right">
                                                                                                            {typeof item.score === 'number' ? item.score.toFixed(2) : item.score}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                        {result.result_data.ranking.length > 5 && (
                                                                                            <p className="text-xs text-gray-400 text-center">+{result.result_data.ranking.length - 5} daha fazla kategori</p>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                {/* Categories Summary */}
                                                                                {result.result_data?.categories && typeof result.result_data.categories === 'object' && !Array.isArray(result.result_data.categories) && (
                                                                                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                                                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tüm Kategoriler</p>
                                                                                        <div className="flex flex-wrap gap-1.5">
                                                                                            {Object.entries(result.result_data.categories).slice(0, 8).map(([key, value]: [string, any]) => (
                                                                                                <span key={key} className="px-2 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-md text-xs">
                                                                                                    {key}: <strong>{typeof value === 'number' ? value.toFixed(2) : value}</strong>
                                                                                                </span>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                {/* Fallback for other result formats */}
                                                                                {!result.result_data?.ranking && !result.result_data?.categories && (
                                                                                    <div className="text-gray-600 dark:text-gray-300">
                                                                                        {typeof result.result_data === 'object' ? (
                                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                                {Object.entries(result.result_data).slice(0, 10).map(([key, value]: [string, any]) => (
                                                                                                    <span key={key} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                                                                                                        {key}: <strong>{typeof value === 'number' ? value.toFixed(2) : String(value)}</strong>
                                                                                                    </span>
                                                                                                ))}
                                                                                            </div>
                                                                                        ) : (
                                                                                            <span>{String(result.result_data)}</span>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>



                                </div>

                                {/* Right Column: Applications & Universitaly & Documents */}
                                <div className="lg:col-span-9 space-y-6">

                                    {/* Schools / Applications Table */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-violet-50/50 dark:bg-violet-900/10">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <School className="w-5 h-5 text-violet-600" />
                                                Okul Başvuruları
                                            </h3>
                                            <button
                                                onClick={() => openModal('app')}
                                                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-violet-500/20">
                                                <Plus className="w-4 h-4" />
                                                Okul Ekle
                                            </button>
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 font-medium border-b border-gray-100 dark:border-gray-700">
                                                    <tr>
                                                        <th className="px-6 py-4">Okul & Bölüm</th>
                                                        <th className="px-6 py-4">Kullanıcı Adı</th>
                                                        <th className="px-6 py-4">Şifre</th>
                                                        <th className="px-6 py-4">Durum</th>
                                                        <th className="px-6 py-4">Ödeme</th>
                                                        <th className="px-6 py-4">Sonuç</th>
                                                        <th className="px-6 py-4"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                    {applications.map((app, index) => (
                                                        <tr key={app.id} className="group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                            {/* School & Department */}
                                                            <td className="px-6 py-4">
                                                                <div className="relative">
                                                                    <input
                                                                        list={`uni-list-${app.id}`}
                                                                        type="text"
                                                                        value={app.school_name}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            const newApps = [...applications];
                                                                            newApps[index].school_name = val;

                                                                            // Try to match ID
                                                                            const match = universities.find(u => u.name.toLowerCase() === val.toLowerCase());
                                                                            newApps[index].university_id = match?.id;

                                                                            setApplications(newApps);
                                                                        }}
                                                                        className="font-bold text-gray-900 dark:text-white bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none w-full mb-1"
                                                                        placeholder="Okul Adı Seç veya Yaz"
                                                                    />
                                                                    <datalist id={`uni-list-${app.id}`}>
                                                                        {universities.map(u => (
                                                                            <option key={u.id} value={u.name} />
                                                                        ))}
                                                                    </datalist>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    value={app.department}
                                                                    onChange={(e) => {
                                                                        const newApps = [...applications];
                                                                        newApps[index].department = e.target.value;
                                                                        setApplications(newApps);
                                                                    }}
                                                                    className="text-gray-500 text-xs bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none w-full"
                                                                    placeholder="Bölüm"
                                                                />
                                                            </td>

                                                            {/* Username */}
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700/50 px-2 py-1 rounded-lg">
                                                                    <User className="w-3 h-3 text-gray-400" />
                                                                    <input
                                                                        type="text"
                                                                        value={app.username || ''}
                                                                        onChange={(e) => {
                                                                            const newApps = [...applications];
                                                                            newApps[index].username = e.target.value;
                                                                            setApplications(newApps);
                                                                        }}
                                                                        className="bg-transparent border-none text-xs text-gray-600 dark:text-gray-300 w-full focus:ring-0 p-0"
                                                                        placeholder="Kullanıcı Adı"
                                                                    />
                                                                </div>
                                                            </td>

                                                            {/* Password */}
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700/50 px-2 py-1 rounded-lg">
                                                                    <Key className="w-3 h-3 text-gray-400" />
                                                                    <input
                                                                        type="text"
                                                                        value={app.password || ''}
                                                                        onChange={(e) => {
                                                                            const newApps = [...applications];
                                                                            newApps[index].password = e.target.value;
                                                                            setApplications(newApps);
                                                                        }}
                                                                        className="bg-transparent border-none text-xs text-gray-600 dark:text-gray-300 w-full focus:ring-0 p-0"
                                                                        placeholder="Şifre"
                                                                    />
                                                                </div>
                                                            </td>

                                                            {/* Status */}
                                                            <td className="px-6 py-4">
                                                                <select
                                                                    value={app.status}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value as any;
                                                                        const newApps = [...applications];
                                                                        newApps[index].status = val;

                                                                        // Update result_status to match the selected status label
                                                                        const statusMap: Record<string, string> = {
                                                                            'pending': 'Bekliyor',
                                                                            'application_made': 'Başvuru Yapıldı',
                                                                            'completed': 'Tamamlandı',
                                                                            'accepted': 'Kabul',
                                                                            'rejected': 'Red',
                                                                            'enrolled': 'Kayıt Oldu'
                                                                        };
                                                                        newApps[index].result_status = statusMap[val] || 'Bekliyor';

                                                                        setApplications(newApps);
                                                                        // Auto-save silently with new state
                                                                        handleSave(true, newApps);
                                                                    }}
                                                                    className={`appearance-none pl-2 pr-6 py-1 rounded-lg text-xs font-bold border focus:outline-none cursor-pointer ${app.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                                        app.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                                                                            app.status === 'accepted' ? 'bg-green-50 text-green-700 border-green-200' :
                                                                                app.status === 'application_made' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                                                    app.status === 'enrolled' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                                                                        'bg-orange-50 text-orange-700 border-orange-200'
                                                                        }`}
                                                                >
                                                                    <option value="pending">Bekliyor</option>
                                                                    <option value="application_made">Başvuru Yapıldı</option>
                                                                    <option value="completed">Tamamlandı</option>
                                                                    <option value="accepted">Kabul</option>
                                                                    <option value="rejected">Red</option>
                                                                    <option value="enrolled">Kayıt Oldu</option>
                                                                </select>
                                                            </td>

                                                            {/* Payment */}
                                                            <td className="px-6 py-4">
                                                                <input
                                                                    type="text"
                                                                    value={app.payment_status || ''}
                                                                    onChange={(e) => {
                                                                        const newApps = [...applications];
                                                                        newApps[index].payment_status = e.target.value;
                                                                        setApplications(newApps);
                                                                    }}
                                                                    className="bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-green-500 focus:outline-none text-xs text-gray-600 dark:text-gray-300 w-24 px-1 py-0.5"
                                                                    placeholder="Ödeme Durumu"
                                                                />
                                                            </td>

                                                            {/* Result */}
                                                            <td className="px-6 py-4">
                                                                <input
                                                                    type="text"
                                                                    value={app.result_status || ''}
                                                                    onChange={(e) => {
                                                                        const newApps = [...applications];
                                                                        newApps[index].result_status = e.target.value;
                                                                        setApplications(newApps);
                                                                    }}
                                                                    className="font-medium text-gray-500 bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none w-full"
                                                                    placeholder="Sonuç"
                                                                />
                                                            </td>

                                                            {/* Delete */}
                                                            <td className="px-6 py-4 text-right">
                                                                <button
                                                                    onClick={async () => {
                                                                        if (await window.appConfirm('Bu başvuruyu silmek istediğinize emin misiniz?')) {
                                                                            const newApps = applications.filter(a => a.id !== app.id);
                                                                            setApplications(newApps);
                                                                        }
                                                                    }}
                                                                    className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>


                                    {/* Universitaly Section - Editable */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-l-orange-500">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <Globe className="w-5 h-5 text-orange-500" />
                                                Universitaly Bilgileri
                                            </h3>

                                            {/* Status Dropdown */}
                                            <div className="relative group">
                                                <select
                                                    value={universitaly?.status || 'Ön Kayıt Aşamasında'}
                                                    onChange={(e) => setUniversitaly({ ...universitaly, status: e.target.value })}
                                                    className={`appearance-none cursor-pointer pl-3 pr-8 py-1.5 rounded-lg text-xs font-bold border focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all ${universitaly?.status === 'Onaylandı'
                                                        ? 'bg-green-50 text-green-700 border-green-200 focus:ring-green-500'
                                                        : universitaly?.status === 'Onaylanmadı'
                                                            ? 'bg-red-50 text-red-700 border-red-200 focus:ring-red-500'
                                                            : 'bg-orange-50 text-orange-700 border-orange-200 focus:ring-orange-500'
                                                        }`}
                                                >
                                                    <option value="Ön Kayıt Aşamasında">Ön Kayıt Aşamasında</option>
                                                    <option value="Onaylandı">Onaylandı</option>
                                                    <option value="Onaylanmadı">Onaylanmadı</option>
                                                </select>
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                                    <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            {/* Username Input */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all">
                                                <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Kullanıcı Adı</span>
                                                <input
                                                    type="text"
                                                    value={universitaly?.username || ''}
                                                    onChange={(e) => setUniversitaly({ ...universitaly, username: e.target.value })}
                                                    placeholder="Kullanıcı adı giriniz"
                                                    className="w-full bg-transparent border-none p-0 text-gray-900 dark:text-white font-medium focus:ring-0 placeholder-gray-300 dark:placeholder-gray-600"
                                                />
                                            </div>

                                            {/* Password Input */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all">
                                                <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Şifre</span>
                                                <input
                                                    type="text"
                                                    value={universitaly?.password || ''}
                                                    onChange={(e) => setUniversitaly({ ...universitaly, password: e.target.value })}
                                                    placeholder="Şifre giriniz"
                                                    className="w-full bg-transparent border-none p-0 text-gray-900 dark:text-white font-medium focus:ring-0 placeholder-gray-300 dark:placeholder-gray-600"
                                                />
                                            </div>

                                            {/* School/Department Input */}
                                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all">
                                                <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Okul / Bölüm</span>
                                                <input
                                                    type="text"
                                                    value={universitaly?.school || ''}
                                                    onChange={(e) => setUniversitaly({ ...universitaly, school: e.target.value })}
                                                    placeholder="Okul adı giriniz"
                                                    className="w-full bg-transparent border-none p-0 text-gray-900 dark:text-white font-medium focus:ring-0 placeholder-gray-300 dark:placeholder-gray-600"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Documents List - Categorized */}
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <FileText className="w-5 h-5 text-blue-600" />
                                                Belgeler
                                            </h3>
                                            <p className="text-xs text-gray-500 mt-1">Her kategori için dosya yükleyebilir ve isimlerini düzenleyebilirsiniz</p>
                                        </div>

                                        <div className="p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {DOCUMENT_CATEGORIES.map(category => {
                                                    const categoryDocs = documents.filter(d => d.document_type === category.key);

                                                    return (
                                                        <div key={category.key} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                                                            {/* Category Header */}
                                                            <div className="flex items-center justify-between mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xl">{category.icon}</span>
                                                                    <span className="font-bold text-sm text-gray-900 dark:text-white">{category.label}</span>
                                                                    {categoryDocs.length > 0 && (
                                                                        <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full font-bold">
                                                                            {categoryDocs.length}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <label className="cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                                                                    <Upload className="w-4 h-4 text-gray-500 hover:text-blue-600" />
                                                                    <input
                                                                        type="file"
                                                                        className="hidden"
                                                                        onChange={(e) => handleFileUpload(e, category.key)}
                                                                    />
                                                                </label>
                                                            </div>

                                                            {/* Category Files */}
                                                            {categoryDocs.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {categoryDocs.map((doc, i) => (
                                                                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50 group">
                                                                            <div className="flex-1 min-w-0">
                                                                                {editingDocId === doc.id ? (
                                                                                    <div className="flex items-center gap-2">
                                                                                        <input
                                                                                            type="text"
                                                                                            value={editingDocName}
                                                                                            onChange={(e) => setEditingDocName(e.target.value)}
                                                                                            className="flex-1 text-xs px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                                                                            autoFocus
                                                                                            onKeyDown={(e) => {
                                                                                                if (e.key === 'Enter') handleSaveDocName(doc);
                                                                                                if (e.key === 'Escape') { setEditingDocId(null); setEditingDocName(''); }
                                                                                            }}
                                                                                        />
                                                                                        <button
                                                                                            onClick={() => handleSaveDocName(doc)}
                                                                                            className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                                                                        >
                                                                                            <Save className="w-3 h-3" />
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate cursor-pointer hover:text-blue-600"
                                                                                        onClick={() => handleRenameDocument(doc)}
                                                                                        title="İsmi düzenlemek için tıklayın"
                                                                                    >
                                                                                        {doc.file_name || doc.name}
                                                                                    </div>
                                                                                )}
                                                                                <div className="text-[10px] text-gray-400 mt-0.5">
                                                                                    {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'dd.MM.yyyy') : ''}
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                                                                                <button
                                                                                    onClick={() => handleDownload(doc)}
                                                                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                                                                    title="İndir"
                                                                                >
                                                                                    <Download className="w-3 h-3" />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => handleDeleteDocument(doc)}
                                                                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                                                    title="Sil"
                                                                                >
                                                                                    <Trash2 className="w-3 h-3" />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-4">
                                                                    <p className="text-[10px] text-gray-400">Dosya yok</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="bg-white dark:bg-gray-900 p-6 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 z-10">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold transition-colors"
                    >
                        Kapat
                    </button>
                    <button
                        onClick={() => handleSave(false)}
                        className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-lg shadow-blue-500/20 transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                    >
                        <Save className="w-5 h-5" />
                        Değişiklikleri Kaydet
                    </button>
                </div>

            </div>

            {/* Add Item Modal Overlay */}
            {activeModal !== 'none' && (
                <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {activeModal === 'exam' ? 'Yeni Sınav Ekle' :
                                    activeModal === 'lang' ? 'Dil Belgesi Ekle' :
                                        activeModal === 'dept' ? 'Bölüm Ekle' : 'Okul Başvurusu Ekle'}
                            </h3>
                            <button onClick={() => setActiveModal('none')} className="text-gray-400 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            {activeModal === 'exam' && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Sınav Adı (Örn: SAT)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.exam_name || ''}
                                        onChange={e => setTempItem({ ...tempItem, exam_name: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Puan (Örn: 1400)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.score || ''}
                                        onChange={e => setTempItem({ ...tempItem, score: e.target.value })}
                                    />
                                    <input
                                        type="date"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.exam_date || ''}
                                        onChange={e => setTempItem({ ...tempItem, exam_date: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Note (Opsiyonel)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.note || ''}
                                        onChange={e => setTempItem({ ...tempItem, note: e.target.value })}
                                    />
                                </>
                            )}

                            {activeModal === 'lang' && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Belge Adı (Örn: IELTS)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.name || ''}
                                        onChange={e => setTempItem({ ...tempItem, name: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Seviye / Puan (Örn: 6.5)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.level || ''}
                                        onChange={e => setTempItem({ ...tempItem, level: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Note / Durum (Opsiyonel)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.note || ''}
                                        onChange={e => setTempItem({ ...tempItem, note: e.target.value })}
                                    />
                                </>
                            )}

                            {activeModal === 'app' && (
                                <>
                                    <div className="relative">
                                        <select
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white appearance-none cursor-pointer"
                                            value={tempItem.school_name || ''}
                                            onChange={e => setTempItem({ ...tempItem, school_name: e.target.value })}
                                        >
                                            <option value="">Okul Seçiniz</option>
                                            {universities.map((u: any) => (
                                                <option key={u.id} value={u.name}>{u.name}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                            <School className="w-5 h-5" />
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Bölüm"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.department || ''}
                                        onChange={e => setTempItem({ ...tempItem, department: e.target.value })}
                                    />
                                </>
                            )}

                            {activeModal === 'deadline' as any && (
                                <>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tarih Türü</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => setTempItem({ ...tempItem, type: 'exam' })}
                                                className={`py-2 rounded-lg text-xs font-bold border transition-all ${(tempItem.type || 'exam') === 'exam'
                                                    ? 'bg-amber-50 border-amber-500 text-amber-700'
                                                    : 'bg-white border-gray-200 text-gray-500'
                                                    }`}
                                            >
                                                Sınav Tarihi
                                            </button>
                                            <button
                                                onClick={() => setTempItem({ ...tempItem, type: 'application' })}
                                                className={`py-2 rounded-lg text-xs font-bold border transition-all ${tempItem.type === 'application'
                                                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                                                    : 'bg-white border-gray-200 text-gray-500'
                                                    }`}
                                            >
                                                Başvuru Tarihi
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Başlık (Örn: Milano Politeknik 1. Alım)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.title || ''}
                                        onChange={e => setTempItem({ ...tempItem, title: e.target.value })}
                                    />
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sınav/Başvuru Tarihi ve Saati</label>
                                        <input
                                            type="datetime-local"
                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 text-gray-900 dark:text-white placeholder-gray-400"
                                            value={tempItem.due_date || ''}
                                            onChange={e => setTempItem({ ...tempItem, due_date: e.target.value })}
                                        />
                                    </div>
                                    <textarea
                                        placeholder="Ek Notlar (Opsiyonel)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 text-gray-900 dark:text-white placeholder-gray-400 min-h-[80px]"
                                        value={tempItem.description || ''}
                                        onChange={e => setTempItem({ ...tempItem, description: e.target.value })}
                                    />
                                    <div className="p-3 bg-green-50 rounded-lg border border-green-100 flex items-start gap-2">
                                        <Globe className="w-4 h-4 text-green-600 mt-0.5" />
                                        <p className="text-[11px] text-green-800 leading-relaxed">
                                            <strong>SMS Bilgilendirmesi:</strong> Belirlediğiniz tarihten <strong>3 gün önce</strong> öğrenciye otomatik hatırlatma mesajı gönderilecektir.
                                        </p>
                                    </div>
                                </>
                            )}

                            {activeModal === 'dept' && (
                                <>
                                    <input
                                        type="text"
                                        placeholder="Bölüm Adı (Örn: Bilgisayar Mühendisliği)"
                                        className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
                                        value={tempItem.dept_name || ''}
                                        onChange={e => setTempItem({ ...tempItem, dept_name: e.target.value })}
                                    />
                                </>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={() => setActiveModal('none')}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleModalSave}
                                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20"
                            >
                                Ekle
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLeadDetail && selectedLead && (
                <LeadDetailModal
                    isOpen={showLeadDetail}
                    onClose={() => setShowLeadDetail(false)}
                    lead={selectedLead}
                    onEdit={() => { }}
                    onUpdateLead={async (leadId, updates) => {
                        // Refresh student/lead data if needed
                        await fetchStudentDetails();
                    }}
                />
            )}
        </div>
    );
};
export default StudentDetailModal;
